import { create } from 'zustand'
import { persist, createJSONStorage } from 'zustand/middleware'

import { DEFAULT_GENERATION_STATUS, GENERATION_STATUS, STORAGE_KEY } from './constants'

// Global, page-wide source of truth for "is a 3D-model generation happening, and
// how did the last one end". It is a read-only projection of the /ai studio's
// generation state (mirrored from a single choke point - see Store/actions/generation
// and Components/Sidebar/Hooks/useGeneratorRunChannel), so any pack on the page can
// consume generation status without importing the studio store. Today the /ai studio
// reads it; the top nav is the intended next consumer.
//
// Persistence + cross-tab sync use zustand's `persist` middleware: it owns the
// localStorage round-trip (so status survives full-page navigation, since there is no
// SPA shell) and auto-writes on every change. Cross-tab sync is not automatic - per
// zustand's docs, `persist` only writes its own tab, so we add the documented
// `withStorageDOMEvents` listener that rehydrates when another tab writes the key.
//
// The /ai studio (`three_d_studio` pack) and the top nav (`fast_navbar` pack) are
// separate webpack entrypoints that share one runtime. A module below splitChunks'
// `minSize` that is imported by both can still be duplicated across their entry
// chunks, which would create two independent stores. Anchoring the browser instance
// on `window` guarantees every pack resolves the same singleton regardless of how
// webpack chunks it.
function createGenerationStatusStore() {
  const initializer = () => ({ ...DEFAULT_GENERATION_STATUS })

  if (typeof window === 'undefined') {
    // Server: single bundle, no storage - a plain module-level instance is correct.
    return create(initializer)
  }

  return create(
    persist(initializer, {
      name: STORAGE_KEY,
      storage: createJSONStorage(() => window.localStorage),
    })
  )
}

// Cross-tab sync: another tab's write fires a `storage` event here; we re-read the key
// via persist's rehydrate(). This is zustand's documented `withStorageDOMEvents` recipe.
// Exported (and returning the rehydrate promise) so it can be unit-tested directly -
// jsdom does not deliver `storage` events to window listeners via dispatchEvent.
export function syncFromStorageEvent(event) {
  if (event?.key !== STORAGE_KEY || !event.newValue) {
    return undefined
  }
  return generationStatusStore.persist?.rehydrate()
}

function resolveGenerationStatusStore() {
  if (typeof window === 'undefined') {
    return createGenerationStatusStore()
  }

  if (!window.cgtGenerationStatusStore) {
    window.cgtGenerationStatusStore = createGenerationStatusStore()
    window.addEventListener('storage', syncFromStorageEvent)
  }

  return window.cgtGenerationStatusStore
}

export const generationStatusStore = resolveGenerationStatusStore()

// zustand's create() returns a hook that also exposes getState/setState/subscribe,
// so the instance and the hook are the same object (mirrors the studio store).
export const useGenerationStatus = generationStatusStore

// Resolves the discrete status a consumer renders from the projected fields.
export const selectStatus = (state) => {
  if (state.isGenerating) {
    return state.isEnhancing ? GENERATION_STATUS.ENHANCING : GENERATION_STATUS.GENERATING
  }

  if (state.lastResult) {
    return state.lastResult.status
  }

  return GENERATION_STATUS.IDLE
}
